// 상점·대장간 공용 그림 부품(2026-10-27 「글뿐인 목록 → 아이콘 카드」): 가격표(금화 그림), 소켓 칸(보석 그림/빈 소켓 그림), 재료 칩(강화석·주문서 그림).
// 그림은 public/assets/ui/items/. 못 읽으면 같은 크기의 CSS 대체 그림이 남아 칸·글자가 깨지지 않는다.
import { assetUrl } from '../pwa/assetMap';
import { GEM_COLORS, gemColorOf } from '../shared/content/consumables';
import { FONT, FONT_NUM, el } from './dom';
import { gemIconPath } from './itemIcons';
import { itemIcon } from './itemIconDom';
import { badgeImg, gearImg } from './gearPanel';
import type { GearCard } from './gearModel';
import { shopRowLayout } from './shopLayout';

export const GOLD_ICON = 'ui/items/cur_gold.webp';
export const SOCKET_EMPTY_ICON = 'ui/items/socket_empty.webp';
const num = (v: number): string => v.toLocaleString('ko-KR');

/** 그림 한 장(못 읽으면 숨기고 onFail 호출) */
function pic(parent: HTMLElement, path: string, px: number, onFail?: () => void): HTMLImageElement {
  const im = el('img', `width:${px}px;height:${px}px;object-fit:contain;flex:0 0 auto;filter:drop-shadow(0 2px 3px rgba(0,0,0,.55));pointer-events:none;`, parent);
  im.alt = ''; im.draggable = false; im.decoding = 'async';
  im.addEventListener('error', () => { im.style.display = 'none'; onFail?.(); }, { once: true });
  im.src = assetUrl(path);
  return im;
}

/** 가격표: 금화 그림 + 숫자(+G). 못 읽으면 CSS 금화 */
export function priceTag(parent: HTMLElement, amount: number, size = 22, color = '#ffd76a', testid?: string): HTMLElement {
  const box = el('div', `display:flex;align-items:center;justify-content:flex-end;gap:5px;white-space:nowrap;font:900 ${size - 5}px ${FONT_NUM};color:${color};`, parent);
  pic(box, GOLD_ICON, size, () => {
    box.insertBefore(el('span', `display:inline-block;flex:0 0 auto;width:${size - 4}px;height:${size - 4}px;border-radius:50%;background:radial-gradient(circle at 35% 30%,#fff6c0,#f2c14e 45%,#a8641a 100%);box-shadow:inset 0 0 0 2px rgba(110,64,8,.65);`), box.firstChild);
  });
  const t = el('span', '', box, num(amount) + 'G');
  if (testid) t.dataset.testid = testid;
  return box;
}

/**
 * 소켓 칸 한 개: 보석(code ≥ 0)이면 그 보석 그림 + 색 테두리, 빈 칸(−1)이면 빈 소켓 그림(점선 마름모 대체 그림).
 * 칸은 항상 정사각 size px
 */
export function socketPip(parent: HTMLElement, code: number, size: number): HTMLElement {
  const filled = code >= 0;
  const col = filled ? GEM_COLORS[gemColorOf(code)].color : 'rgba(174,185,218,.45)';
  const box = el('span', `position:relative;display:grid;place-items:center;flex:0 0 auto;width:${size}px;height:${size}px;box-sizing:border-box;border-radius:${Math.round(size * 0.24)}px;` +
    `border:${filled ? 2 : 1.5}px ${filled ? 'solid' : 'dashed'} ${col};background:radial-gradient(circle at 50% 40%,${filled ? 'rgba(255,255,255,.14)' : 'rgba(255,255,255,.05)'},rgba(6,9,20,.75) 72%);box-shadow:inset 0 0 8px rgba(0,0,0,.55);`, parent);
  box.dataset.socket = filled ? 'gem' : 'empty';
  const path = filled ? gemIconPath(code) : SOCKET_EMPTY_ICON;
  const inner = Math.round(size * 0.84);
  if (path) {
    pic(box, path, inner, () => {
      // 그림을 못 읽으면 같은 칸 안에 CSS 마름모(채움 = 보석 색, 빈 칸 = 속이 빈 마름모)
      const d = Math.round(size * 0.42);
      el('span', `width:${d}px;height:${d}px;transform:rotate(45deg);border:2px solid ${filled ? '#fff' : col};background:${filled ? col : 'transparent'};`, box);
    });
  }
  return box;
}

/** 재료 칩: 그림 + 수(없으면 흐리게). 강화석 등급별·보호 주문서 */
export function matChip(parent: HTMLElement, path: string, label: string, count: number, size = 34, testid?: string): HTMLElement {
  const c = el('span', `display:inline-flex;align-items:center;gap:4px;padding:2px 9px 2px 3px;border-radius:10px;background:rgba(10,14,30,.75);border:1px solid rgba(174,185,218,.28);${count > 0 ? '' : 'opacity:.55;'}`, parent);
  c.title = label;
  c.setAttribute('aria-label', label + ' ' + count);
  if (testid) c.dataset.testid = testid;
  pic(c, path, size);
  el('span', `font:900 13px ${FONT_NUM};color:#e6ecff;`, c, '×' + num(count));
  return c;
}

/** 칸 위 작은 글자 배지(Lv·신규 등) */
export function cornerTag(parent: HTMLElement, text: string, pos: 'bl' | 'br' | 'tl' | 'tr' = 'br'): HTMLElement {
  // 배지는 부모 칸 기준으로 놓인다(칸이 position을 안 가졌으면 창 모서리로 날아간다)
  if (!parent.style.position) parent.style.position = 'relative';
  const p = pos === 'bl' ? 'left:2px;bottom:2px;' : pos === 'br' ? 'right:2px;bottom:2px;' : pos === 'tl' ? 'left:2px;top:2px;' : 'right:2px;top:2px;';
  return el('span', `position:absolute;${p}padding:0 5px;border-radius:5px;background:rgba(6,9,20,.82);border:1px solid rgba(242,193,78,.5);color:#ffe7a0;font:900 11px/16px ${FONT};pointer-events:none;`, parent, text);
}

/** 상품 카드 한 줄의 틀: 왼쪽 그림 칸, 가운데 글 영역, 오른쪽(또는 좁으면 아래) 가격·단추 영역 */
export interface ProductRow {
  readonly row: HTMLElement;
  readonly text: HTMLElement;
  /** 가격·단추를 넣는 영역(가로 배치면 오른쪽 열, 좁으면 아래 줄) */
  readonly buy: HTMLElement;
  readonly stack: boolean;
}
/** bodyW(설계 px)에 맞춰 상품 카드 틀을 만든다. 구매 영역은 카드 안에 있어 창 오른쪽 아래의 다른 단추에 가리지 않는다 */
export function productRow(parent: HTMLElement, bodyW: number, o: { testid: string; color: string; iconPath: string | null; buyW?: number; border?: string; tag?: string }): ProductRow {
  const lay = shopRowLayout(bodyW, o.buyW);
  const row = el('div', `--gc:${o.color};display:grid;grid-template-columns:${lay.columns};gap:6px 10px;align-items:center;` + (o.border ? `border-color:${o.border};` : ''), parent);
  row.className = 'dh-g-card';
  row.dataset.testid = o.testid;
  row.dataset.layout = lay.stack ? 'stack' : 'side';
  const pic = itemIcon(row, o.iconPath, lay.icon, o.color, false);
  if (pic && o.tag) cornerTag(pic, o.tag, 'br');
  if (!pic) el('div', `width:${lay.icon}px;height:${lay.icon}px;`, row);
  const text = el('div', 'min-width:0;display:flex;flex-direction:column;gap:2px;', row);
  const buy = el('div', lay.stack
    ? 'grid-column:1/-1;display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;'
    : 'display:flex;flex-direction:column;align-items:stretch;gap:6px;', row);
  buy.dataset.testid = o.testid + '-buy';
  return { row, text, buy, stack: lay.stack };
}

/** 장비 그림 칸(소켓 장비 목록·강화 상세 공용): 등급 테두리·장비 그림·+강화 배지·장착 E·세트/전설 배지. 항상 정사각 size px */
export function gearThumb(parent: HTMLElement, c: GearCard, size: number): HTMLElement {
  const box = el('div', `--gc:${c.color};position:relative;flex:0 0 auto;width:${size}px;height:${size}px;box-sizing:border-box;border-radius:10px;border:2px solid ${c.color};overflow:hidden;` +
    `background:radial-gradient(circle at 50% 38%,color-mix(in srgb,${c.color} 34%,#1b2440) 0%,#121a33 62%,#0b1022 100%);box-shadow:inset 0 0 12px rgba(0,0,0,.55);`, parent);
  box.dataset.testid = 'gear-thumb';
  const im = gearImg(box, c.icon, c.iconFallbacks);
  im.style.cssText = 'position:absolute;inset:7%;width:86%;height:86%;object-fit:contain;filter:drop-shadow(0 2px 3px rgba(0,0,0,.6));pointer-events:none;';
  if (c.badge) { const b = badgeImg(box, c.badge); b.style.cssText = 'position:absolute;right:2px;bottom:2px;width:34%;height:34%;pointer-events:none;'; }
  if (c.enh > 0) el('span', `position:absolute;right:3px;top:2px;font:900 ${Math.max(11, Math.round(size * 0.2))}px ${FONT_NUM};color:#fff;text-shadow:0 1px 2px #000,0 0 4px #000;`, box, '+' + c.enh);
  if (c.equipped) el('span', `position:absolute;left:3px;top:3px;padding:0 4px;border-radius:5px;background:#f2c14e;color:#1a1206;font:900 10px/15px ${FONT};`, box, 'E');
  return box;
}
